import React from 'react';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { getColorFromPercentage } from '../utils/colorUtils';

ChartJS.register(ArcElement, Tooltip, Legend);

const CategoryPointsLostChart = ({ categories }) => {
  const maxScore = 5.0;
  
  // Helper function to generate a unique ID from category name
  const getCategoryId = (categoryName) => {
    return `category-${categoryName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`;
  };

  // Handle click to scroll to category details
  const handleChartClick = (event, elements) => {
    if (elements.length > 0) {
      const clickedIndex = elements[0].index;
      const clickedCategory = pointsLostData[clickedIndex];
      if (clickedCategory) {
        const categoryId = getCategoryId(clickedCategory.name);
        const element = document.getElementById(categoryId);
        if (element) {
          element.scrollIntoView({ block: 'start' });
        }
      }
    }
  };
  
  // Calculate points lost for each category
  const pointsLostData = categories
    .map(cat => {
      const isNA = cat.average === 'N/A' || cat.average === null || cat.average === undefined;
      const average = isNA ? 0 : (typeof cat.average === 'number' ? cat.average : parseFloat(cat.average) || 0);
      const pointsLost = maxScore - average;
      return {
        name: cat.name,
        pointsLost: pointsLost,
        average: average,
        isNA: isNA
      };
    })
    .filter(item => !item.isNA && item.pointsLost > 0) // Only show categories that lost points (exclude N/A)
    .sort((a, b) => b.pointsLost - a.pointsLost); // Sort by points lost descending (highest first)

  if (pointsLostData.length === 0) {
    return null; // Don't show chart if no points were lost
  }

  // Calculate total points lost
  const totalPointsLost = pointsLostData.reduce((sum, item) => sum + item.pointsLost, 0);

  // Calculate percentages and prepare chart data
  const chartData = {
    labels: pointsLostData.map(item => item.name),
    datasets: [{
      data: pointsLostData.map(item => item.pointsLost),
      backgroundColor: pointsLostData.map(item => {
        const percentage = (item.average / maxScore) * 100;
        return getColorFromPercentage(percentage);
      }),
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: handleChartClick,
    layout: {
      padding: {
        top: 40,
        bottom: 40,
        left: 120,
        right: 120
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const percentage = totalPointsLost > 0 ? ((value / totalPointsLost) * 100).toFixed(1) : 0;
            return `${label}: ${percentage}%`;
          }
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#121b29',
        bodyColor: '#27303e',
        borderColor: '#dce2ec',
        borderWidth: 1,
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 12
        }
      },
      datalabels: {
        color: '#27303e',
        anchor: 'end',
        align: 'end',
        offset: 10,
        clip: false,
        font: {
          weight: '600',
          size: 11
        },
        formatter: (value, ctx) => {
          const percentage = totalPointsLost > 0 ? ((value / totalPointsLost) * 100).toFixed(1) : 0;
          if (parseFloat(percentage) < 4) return '';
          
          let label = ctx.chart.data.labels[ctx.dataIndex];
          // Strip the leading number and dot (e.g. "1. Code Quality..." -> "Code Quality...")
          label = label.replace(/^\d+\.\s*/, '');
          if (label.length > 25) {
            label = label.substring(0, 25) + '...';
          }
          return `${label}\n${percentage}%`;
        },
        textAlign: 'center'
      }
    }
  };

  return (
    <div className="p-6 m-0 bg-transparent border-0 shadow-none text-center">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-5">Points Lost by Category</h3>
      <div className="mx-auto h-[400px] w-full relative overflow-visible">
        <Pie data={chartData} options={chartOptions} plugins={[ChartDataLabels]} style={{ cursor: 'pointer' }} />
      </div>
    </div>
  );
};

export default CategoryPointsLostChart;
